import * as R from 'ramda';
import { assoc, dissoc } from './module';


export default class ImmutableMap<TValue> {
  public static empty = <T>() => new ImmutableMap<T>();


  public static equals = <T>(
    first: ImmutableMap<T>,
    second: ImmutableMap<T>,
  ): boolean => first === second || (first && first.equals(second)) || (second && second.equals(first));


  private values = {};


  private setMap = (newValue): ImmutableMap<TValue> => {
    this.values = newValue;
    return this;
  };


  private adjustMap = (newValue) => (new ImmutableMap<TValue>()).setMap(newValue);


  public toJSON() {
    return JSON.stringify(this.values);
  }


  public toString() {
    return JSON.stringify(this.values);
  }


  /**
   * Produces a new map with the given key and its value. If the key already exists in the map, the old value is
   * replaced with the new value.
   */
  public add = (
    key: string,
    value: TValue,
  ): ImmutableMap<TValue> => this.adjustMap(
    assoc(
      // @ts-ignore
      key,
      value,
      this.values,
    ),
  );


  /**
   * Produces a new map without the given key and its value
   */
  public remove = (key: string): ImmutableMap<TValue> => this.adjustMap(
    dissoc(
      // @ts-ignore
      key,
      this.values,
    ),
  );


  /**
   * Takes the given map and merges it onto this map to produce a new map. Key collisions are resolved with the values
   * from the given map replacing the values in this map. If the given map is null or undefined, you just get this map
   * back.
   */
  public merge = (
    other: ImmutableMap<TValue> | undefined | null,
  ): ImmutableMap<TValue> => other ? this.adjustMap(R.mergeDeepRight(this.values, other.values)) : this;


  /**
   * Returns the value in the map for the given key. If the key doesn't exist in the map, returns `undefined`.
   */
  public get = (key: string): TValue => this.values[key];


  /**
   * Returns whether or not the map has an entry for the given key.
   */
  public has = (key: string): boolean => !R.isNil(this.values[key]);


  /**
   * Gets the value for the given key from the map if it exists. If the value doesn't exist, returns the given default.
   */
  public getOrDefault = (key: string, defaultValue: TValue): TValue => this.has(key) ? this.get(key) : defaultValue;


  /**
   * Returns an array of all of the keys in the map.
   */
  public getKeys = (): string[] => R.keys(this.values);


  /**
   * Returns an array of all of the values in this ImmutableMap. If the values are references, and you mutate one, then
   * this map will also be mutated.
   */
  public getValues = (): TValue[] => this.getKeys().map(key => this.get(key));


  /**
   * How many values are in this map
   */
  public count = (): number => this.getKeys().length;


  /**
   * Whether or not there are values in this map
   */
  public isEmpty = (): boolean => this.count() === 0;


  /**
   * Returns whether or not the values from this ImmutableMap equal the values of the given ImmutableMap
   */
  public equals = (
    other: ImmutableMap<TValue> | undefined | null,
  ): boolean => !!other && R.equals(other.values, this.values);


  /**
   * Applies the given map function to the members of this ImmutableMap to produce an array of mapped values.
   * Note: this is Immutable if and only if your map function does not mutate the values it is given (only applies when
   * the value type is a reference object)
   */
  public map = <TReturn>(
    mapFunc: (key: string, value: TValue) => TReturn,
  ): TReturn[] => this.getKeys().map(key => mapFunc(key, this.get(key)));


  /**
   * Applies the given map function to the members of this ImmutableMap to produce a new ImmutableMap with the same keys
   * but with the new mapped values. Note: this is Immutable if and only if your map function does not mutate the values
   * it is given (only applies when the value type is a reference object)
   */
  public mapInto = <TReturn>(
    mapFunc: (key: string, value: TValue) => TReturn,
  ): ImmutableMap<TReturn> => new ImmutableMap<TReturn>().setMap(
    R.mapObjIndexed((value: TValue, key) => mapFunc(key, value), this.values),
  );


  /**
   * Creates a new immutable map with the key/value pairs removed that did not pass the given predicate.
   */
  public filter = (
    predicate: (key: string, value: TValue) => boolean,
  ): ImmutableMap<TValue> => this.adjustMap(
    this.getKeys().reduce(
      (running, nextKey) => predicate(nextKey, this.get(nextKey))
        // @ts-ignore
        ? assoc(nextKey, this.get(nextKey), running)
        : running,
      {},
    ),
  );


  /**
   * Accumulate a sum value out of this ImmutableMap
   */
  public reduce = <TReduction>(
    accumulator: ((running: TReduction, nextKey: string, nextValue: TValue) => TReduction),
    startingValue: TReduction,
  ): TReduction => this.getKeys().reduce((running, next) => accumulator(running, next, this.get(next)), startingValue);


  /**
   * Returns whether or not any members of the map match the given predicate
   */
  public any = (
    predicate: (key: string, value: TValue) => boolean,
  ): boolean => R.any(key => predicate(key, this.get(key)), this.getKeys());


  /**
   * Returns the number of elements in the map matching the given predicate
   * @param predicate - the function that says whether or not to count a specific item
   */
  public countWhere = (
    predicate: (key: string, value: TValue) => boolean,
  ): number => this.reduce((running, key, value) => predicate(key, value) ? running + 1 : running, 0);


  /**
   * Takes a given key and a mapping function, associates the mapped value. If the key isn't in the map, you'll get an
   * undefined as the argument to your mapping function. If your mapping function returns undefined, it won't be stored
   * to the map, meaning the original map will be returned.
   * @param key - the map key to associate against
   * @param assocFn - The function returning a new value to put into the map for the given key
   */
  public assocIn = (
    key: string,
    assocFn: (value: TValue | undefined) => TValue | undefined,
  ): ImmutableMap<TValue> => {
    const newValue = assocFn(this.get(key));

    if (!newValue) {
      return this;
    }

    return this.add(key, newValue);
  };
}
